import {
	Close as CloseIcon,
	Pause as PauseIcon,
	PlayArrow as PlayArrowIcon,
	SkipPrevious as SkipPreviousIcon,
} from "@mui/icons-material";
import {
	Box,
	Button,
	Chip,
	IconButton,
	LinearProgress,
	Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import Joyride, { ACTIONS, EVENTS, STATUS } from "react-joyride";

/**
 * Reusable, config-driven guided tour for the analytics dashboards.
 *
 * It encapsulates the exact tour "chrome" first implemented for the Vehicle
 * Tracking dashboard (VffDashboardJoyrideTour): react-joyride + a custom
 * dark-navy tooltip, progress bar, overlay/spotlight styling and the
 * Previous / Next / Skip Tour / Pause / Close controls. Only the content is
 * supplied via props, so the same component can drive the Supply Type
 * Allocation tour today and the upcoming Series Tracking tour later.
 *
 * The Welcome screen is always shown first and is NOT counted as a feature
 * step: the step counter and progress bar reflect the feature steps only.
 *
 * Props:
 *  - isOpen:    boolean — controls whether the tour runs.
 *  - onClose:   () => void — called when the tour finishes/skips/closes.
 *  - tourTitle: string — shown in the tooltip header (e.g. "Supply Type
 *               Allocation Tour").
 *  - welcome:   { icon?, title, description, footer?, chips?: [{icon?, label,
 *               bgcolor?}] } — content of the Welcome screen.
 *  - steps:     Array<{ target, placement?, icon?, title, description,
 *               extra?: { heading, text } }> — the feature steps that
 *               highlight real DOM elements (via data-tour-target selectors).
 */
const DashboardGuidedTour = ({ isOpen, onClose, tourTitle, welcome, steps }) => {
	const [run, setRun] = useState(false);
	const [stepIndex, setStepIndex] = useState(0);
	const [isPlaying, setIsPlaying] = useState(true);

	const featureSteps = steps || [];
	const welcomeChips = (welcome && welcome.chips) || [];

	// Build the react-joyride steps: index 0 is the Welcome screen (no
	// highlight); the remaining entries highlight actual dashboard elements.
	const joyrideSteps = [
		{
			target: "body",
			content: (
				<Box sx={{ p: 3, maxWidth: 450 }}>
					<Typography
						variant="h5"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						{welcome?.icon}
						{welcome?.title}
					</Typography>
					<Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
						{welcome?.description}
					</Typography>
					{welcomeChips.length > 0 && (
						<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
							{welcomeChips.map((chip) => (
								<Chip
									key={chip.label}
									icon={chip.icon}
									label={chip.label}
									size="small"
									sx={{ bgcolor: chip.bgcolor || "#e3f2fd" }}
								/>
							))}
						</Box>
					)}
					<Typography
						variant="body2"
						sx={{ color: "#666", fontStyle: "italic" }}
					>
						{welcome?.footer ||
							"This tour will guide you through all key features in about 3 minutes."}
					</Typography>
				</Box>
			),
			placement: "center",
			disableBeacon: true,
			styles: {
				options: {
					zIndex: 10000,
				},
			},
		},
		...featureSteps.map((step) => ({
			target: step.target,
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						{step.icon}
						{step.title}
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						{step.description}
					</Typography>
					{step.extra && (
						<Box
							sx={{
								bgcolor: "#f8f9fa",
								borderRadius: 1,
								p: 1.5,
								border: "1px solid #e0e0e0",
							}}
						>
							<Typography
								variant="caption"
								sx={{ fontWeight: "bold", color: "#cc0000" }}
							>
								{step.extra.heading}
							</Typography>
							<Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
								{step.extra.text}
							</Typography>
						</Box>
					)}
				</Box>
			),
			placement: step.placement || "bottom",
			spotlightPadding: 8,
			disableScrolling: true,
		})),
	];

	// Number of feature steps (everything after the Welcome screen).
	const featureStepCount = joyrideSteps.length - 1;
	const isWelcome = stepIndex === 0;
	// 1-based position of the current feature step (0 while on the Welcome screen).
	const featureStepNumber = isWelcome ? 0 : stepIndex;
	// Progress reflects the feature steps only; the Welcome screen sits at 0%.
	const progress =
		isWelcome || featureStepCount === 0
			? 0
			: (featureStepNumber / featureStepCount) * 100;

	useEffect(() => {
		if (isOpen) {
			setRun(true);
			setStepIndex(0);
			setIsPlaying(true);
		} else {
			setRun(false);
		}
	}, [isOpen]);

	const handleJoyrideCallback = useCallback(
		(data) => {
			const { action, index, status, type } = data;

			if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
				const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);
				setStepIndex(nextStepIndex);
			} else if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
				setRun(false);
				onClose();
			}
		},
		[onClose],
	);

	const handleClose = () => {
		// The close (X) button skips the entire tour.
		setRun(false);
		onClose();
	};

	const handleSkipTour = () => {
		setRun(false);
		onClose();
	};

	const handlePlay = () => {
		setIsPlaying(!isPlaying);
		setRun(!isPlaying);
	};

	// Custom tooltip component (identical design to VffDashboardJoyrideTour).
	const CustomTooltip = ({
		index,
		step,
		backProps,
		primaryProps,
		tooltipProps,
	}) => (
		<Box
			{...tooltipProps}
			sx={{
				backgroundColor: "white",
				borderRadius: "12px",
				boxShadow: "0 8px 32px rgba(30, 40, 50, 0.15)",
				maxWidth: step.content.props.sx?.maxWidth || 400,
				overflow: "hidden",
				fontFamily: "MAN EUROPE_Regular",
			}}
		>
			{/* Header */}
			<Box
				sx={{
					backgroundColor: "#1e2832",
					color: "white",
					p: 2,
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<Typography
					variant="h6"
					sx={{
						fontWeight: "bold",
						fontSize: "14px",
						fontFamily: "MAN EUROPE_Bold",
					}}
				>
					{index === 0
						? tourTitle
						: `${tourTitle} (${index}/${featureStepCount})`}
				</Typography>
				<Box sx={{ display: "flex", gap: 1 }}>
					<IconButton
						{...backProps}
						size="small"
						disabled={index === 0}
						sx={{ color: index === 0 ? "#666" : "white" }}
					>
						<SkipPreviousIcon fontSize="small" />
					</IconButton>
					<IconButton onClick={handlePlay} size="small" sx={{ color: "white" }}>
						{isPlaying ? (
							<PauseIcon fontSize="small" />
						) : (
							<PlayArrowIcon fontSize="small" />
						)}
					</IconButton>
					<IconButton
						onClick={handleClose}
						size="small"
						sx={{ color: "white" }}
						title="Skip Tour"
					>
						<CloseIcon fontSize="small" />
					</IconButton>
				</Box>
			</Box>
			{/* Progress Bar */}
			<Box sx={{ px: 2, pt: 1 }}>
				<LinearProgress
					variant="determinate"
					value={progress}
					sx={{
						height: 4,
						borderRadius: 2,
						backgroundColor: "#e0e0e0",
						"& .MuiLinearProgress-bar": {
							backgroundColor: "#cc0000",
						},
					}}
				/>
				<Typography
					variant="caption"
					sx={{
						display: "block",
						textAlign: "center",
						mt: 0.5,
						color: "#666",
						fontSize: "12px",
					}}
				>
					{index === 0
						? "Welcome"
						: `Step ${featureStepNumber} of ${featureStepCount}`}
				</Typography>
			</Box>
			{/* Content */}
			<Box sx={{ p: 0 }}>{step.content}</Box>
			{/* Footer */}
			<Box
				sx={{
					p: 2,
					borderTop: "1px solid #eee",
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					backgroundColor: "#f8f9fa",
				}}
			>
				<Button
					onClick={handleSkipTour}
					variant="text"
					sx={{
						color: "#666",
						fontSize: "12px",
						fontFamily: "MAN EUROPE_Regular",
					}}
				>
					Skip Tour
				</Button>
				<Box sx={{ display: "flex", gap: 1 }}>
					{index > 0 && (
						<Button
							{...backProps}
							variant="outlined"
							size="small"
							sx={{
								borderColor: "#cc0000",
								color: "#cc0000",
								fontSize: "12px",
								fontFamily: "MAN EUROPE_Bold",
								"&:hover": {
									borderColor: "#aa0000",
									backgroundColor: "#ffebee",
								},
							}}
						>
							Previous
						</Button>
					)}
					<Button
						{...primaryProps}
						variant="contained"
						size="small"
						sx={{
							backgroundColor: "#cc0000",
							fontSize: "12px",
							fontFamily: "MAN EUROPE_Bold",
							"&:hover": {
								backgroundColor: "#aa0000",
							},
						}}
					>
						{index === joyrideSteps.length - 1 ? "Finish" : "Next"}
					</Button>
				</Box>
			</Box>
		</Box>
	);

	return (
		<>
			{/* Custom CSS to fix spotlight visibility */}
			<style>{`
        .__floater__arrow polygon {
          fill: #fff !important;
        }

        /* Fix spotlight visibility - remove grey blur */
        [data-tour-elem="spotlight"] {
          background-color: transparent !important;
          filter: none !important;
          opacity: 1 !important;
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
        }

        .react-joyride__spotlight {
          background-color: transparent !important;
          filter: none !important;
          opacity: 1 !important;
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
          mix-blend-mode: normal !important;
          border: none !important;
          box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.4),
            0 0 16px 3px rgba(255, 255, 255, 0.4) !important;
        }

        .react-joyride__overlay {
          mix-blend-mode: normal !important;
          background-color: rgba(0, 0, 0, 0.4) !important;
        }

        /* Ensure target elements are fully visible */
        .react-joyride__spotlight > * {
          filter: none !important;
          opacity: 1 !important;
          visibility: visible !important;
        }

        /* Remove any filters that might cause blur */
        body.react-joyride__body--scrolling * {
          filter: none !important;
        }

        /* Ensure proper z-index for highlighted elements */
        .react-joyride__spotlight {
          z-index: 9998 !important;
        }

        .react-joyride__tooltip {
          z-index: 9999 !important;
        }
      `}</style>
			<Joyride
				callback={handleJoyrideCallback}
				continuous={true}
				run={run && isPlaying}
				scrollToFirstStep={true}
				showProgress={false}
				showSkipButton={true}
				stepIndex={stepIndex}
				steps={joyrideSteps}
				tooltipComponent={CustomTooltip}
				disableScrolling={true}
				spotlightClicks={true}
				disableOverlayClose={true}
				styles={{
					options: {
						arrowColor: "#fff",
						backgroundColor: "#fff",
						overlayColor: "rgba(0, 0, 0, 0.4)",
						primaryColor: "#cc0000",
						textColor: "#333",
						width: undefined,
						zIndex: 10000,
					},
					overlay: {
						mixBlendMode: "normal",
						backgroundColor: "rgba(0, 0, 0, 0.4)",
					},
					spotlight: {
						borderRadius: "8px",
						border: "none",
						backgroundColor: "transparent",
						boxShadow:
							"0 0 0 9999px rgba(0, 0, 0, 0.4), 0 0 16px 3px rgba(255, 255, 255, 0.4)",
						filter: "none !important",
						opacity: 1,
						mixBlendMode: "normal",
					},
					spotlightLegacy: {
						borderRadius: "8px",
						backgroundColor: "transparent",
						filter: "none",
					},
				}}
				locale={{
					back: "Previous",
					close: "Close",
					last: "Finish",
					next: "Next",
					skip: "Skip Tour",
				}}
			/>
		</>
	);
};

export default DashboardGuidedTour;
